import { useState, useEffect, useCallback } from 'react';
import { useHotelConfig } from './useHotelConfig';
import { logger } from '../lib/logger';
import { stayService } from '../lib/stay/stayService';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';
const STORAGE_KEY = 'push_notification_status';

type NotificationStatus = 'idle' | 'granted' | 'denied' | 'unsupported';

interface UsePushNotificationsReturn {
  status: NotificationStatus;
  isSupported: boolean;
  isSubscribed: boolean;
  subscriberCount: number | null;
  requestPermission: () => Promise<boolean>;
  unsubscribe: () => Promise<void>;
  sendNotification: (params: SendNotificationParams) => Promise<SendResult>;
  loadSubscriberCount: () => Promise<void>;
}

export interface SendNotificationParams {
  title: string;
  body: string;
  url?: string;
  icon?: string;
  sent_by?: string;
}

interface SendResult {
  success: boolean;
  total_sent?: number;
  total_success?: number;
  total_failed?: number;
  error?: string;
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

async function callEdgeFunction(slug: string, method: string, body?: object, queryParams?: string) {
  const url = `${supabaseUrl}/functions/v1/${slug}${queryParams ? `?${queryParams}` : ''}`;
  const res = await fetch(url, {
    method,
    headers: {
      'Authorization': `Bearer ${supabaseAnonKey}`,
      'Content-Type': 'application/json',
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  return res.json();
}

export function usePushNotifications(): UsePushNotificationsReturn {
  const { id: hotelId } = useHotelConfig();
  const isSupported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

  const [status, setStatus] = useState<NotificationStatus>(() => {
    if (!isSupported) return 'unsupported';
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'granted') return 'granted';
    if (Notification.permission === 'granted') return 'granted';
    if (Notification.permission === 'denied') return 'denied';
    return 'idle';
  });

  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriberCount, setSubscriberCount] = useState<number | null>(null);

  const getServiceWorkerRegistration = useCallback(async () => {
    if (!('serviceWorker' in navigator)) return null;
    const registrations = await navigator.serviceWorker.getRegistrations();
    if (registrations.length > 0) return registrations[0];
    return null;
  }, []);

  useEffect(() => {
    if (!isSupported) return;
    (async () => {
      const reg = await getServiceWorkerRegistration();
      if (!reg) return;
      const sub = await reg.pushManager.getSubscription();
      setIsSubscribed(!!sub);
    })();
  }, [isSupported, getServiceWorkerRegistration]);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isSupported) return false;
    if (!VAPID_PUBLIC_KEY) {
      logger.warn('[PushNotif] VITE_VAPID_PUBLIC_KEY not set');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setStatus('denied');
        localStorage.setItem(STORAGE_KEY, 'denied');
        return false;
      }

      const reg = await getServiceWorkerRegistration();
      if (!reg) {
        logger.warn('[PushNotif] No service worker registration found');
        return false;
      }

      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      const key = subscription.getKey('p256dh');
      const authKey = subscription.getKey('auth');
      if (!key || !authKey) return false;

      const p256dh = btoa(String.fromCharCode(...new Uint8Array(key)));
      const auth = btoa(String.fromCharCode(...new Uint8Array(authKey)));

      const stayDates = stayService.getStayDates();
      const subscriptionPayload: Record<string, unknown> = {
        hotel_id: hotelId,
        endpoint: subscription.endpoint,
        p256dh,
        auth,
        user_agent: navigator.userAgent,
        language: navigator.language || 'fr',
      };
      if (stayDates) {
        subscriptionPayload.check_in = stayDates.startDate.split('T')[0];
        subscriptionPayload.check_out = stayDates.endDate.split('T')[0];
      }

      await callEdgeFunction('save-push-subscription', 'POST', subscriptionPayload);

      setStatus('granted');
      setIsSubscribed(true);
      localStorage.setItem(STORAGE_KEY, 'granted');
      return true;
    } catch (err) {
      logger.warn('[PushNotif] Error subscribing:', { data: { err } });
      return false;
    }
  }, [isSupported, hotelId, getServiceWorkerRegistration]);

  const unsubscribe = useCallback(async () => {
    try {
      const reg = await getServiceWorkerRegistration();
      if (!reg) return;
      const sub = await reg.pushManager.getSubscription();
      if (!sub) return;

      await callEdgeFunction('save-push-subscription', 'DELETE', {
        endpoint: sub.endpoint,
      });

      await sub.unsubscribe();
      setStatus('idle');
      setIsSubscribed(false);
      localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      logger.warn('[PushNotif] Error unsubscribing:', { data: { err } });
    }
  }, [getServiceWorkerRegistration]);

  const sendNotification = useCallback(async (params: SendNotificationParams): Promise<SendResult> => {
    try {
      const result = await callEdgeFunction('send-push-notification', 'POST', {
        hotel_id: hotelId,
        ...params,
        sent_by: params.sent_by || 'admin',
      });
      return result;
    } catch (err) {
      return { success: false, error: String(err) };
    }
  }, [hotelId]);

  const loadSubscriberCount = useCallback(async () => {
    try {
      const result = await callEdgeFunction(
        'save-push-subscription',
        'GET',
        undefined,
        `hotel_id=${hotelId}`
      );
      setSubscriberCount(result.count ?? 0);
    } catch {
      setSubscriberCount(null);
    }
  }, [hotelId]);

  return {
    status,
    isSupported,
    isSubscribed,
    subscriberCount,
    requestPermission,
    unsubscribe,
    sendNotification,
    loadSubscriberCount,
  };
}
